/** Tiny Web Audio sound bank: preload WAVs, play with overlap and pitch. */
export default class Sound {
  private ctx?: AudioContext
  private readonly buffers = new Map<string, AudioBuffer>()

  constructor(sources: Record<string, string>) {
    void this.load(sources)
  }

  private get context(): AudioContext {
    if (!this.ctx) this.ctx = new AudioContext()
    return this.ctx
  }

  private async load(sources: Record<string, string>): Promise<void> {
    await Promise.all(
      Object.entries(sources).map(async ([key, url]) => {
        try {
          const res = await fetch(url)
          if (!res.ok) throw new Error(`HTTP ${res.status}`)
          const buf = await this.context.decodeAudioData(
            await res.arrayBuffer(),
          )
          this.buffers.set(key, buf)
        } catch (error) {
          console.warn(`[cs] sound "${url}" failed:`, error)
        }
      }),
    )
  }

  /** Resume the audio context — call from a user gesture (e.g. the first click). */
  public resume(): void {
    void this.context.resume()
  }

  public play(key: string, volume = 1, rate = 1): void {
    const buffer = this.buffers.get(key)
    if (!buffer || this.context.state !== 'running') return
    const src = this.context.createBufferSource()
    src.buffer = buffer
    src.playbackRate.value = rate
    const gain = this.context.createGain()
    gain.gain.value = volume
    src.connect(gain).connect(this.context.destination)
    src.start()
  }

  /** Play a random key from the list (e.g. footstep variants). */
  public playOneOf(keys: string[], volume = 1, rate = 1): void {
    const k = keys[Math.floor(Math.random() * keys.length)]
    this.play(k, volume, rate)
  }
}
